import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Check, ChevronLeft, ChevronRight, ArrowLeft, CheckCircle2, Circle } from "lucide-react";

export default function CoursePlayer() {
  const { user, loading } = useAuth();
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState({ completed_lesson_ids: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    if (!user.enrolled) { navigate("/dashboard"); return; }
    api.get("/course").then(({ data }) => setCourse(data));
    api.get("/progress").then(({ data }) => setProgress(data));
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!lessonId) return;
    api.get(`/course/lesson/${lessonId}`).then(({ data }) => setLesson(data)).catch(() => setLesson(null));
  }, [lessonId]);

  const flat = useMemo(() => {
    if (!course) return [];
    return course.modules.flatMap(m => m.lessons.map(l => ({ ...l, module: m })));
  }, [course]);

  const idx = flat.findIndex(l => l.id === lessonId);
  const prevLesson = idx > 0 ? flat[idx - 1] : null;
  const nextLesson = idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null;

  const isComplete = progress.completed_lesson_ids.includes(lessonId);

  const toggleComplete = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/progress", { lesson_id: lessonId, completed: !isComplete });
      const { data } = await api.get("/progress");
      setProgress(data);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !course || !user) {
    return <div className="min-h-screen bg-[#F7F6F0] p-12 font-mono-plex text-xs uppercase tracking-[0.3em] text-[#4B534E]">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#F7F6F0] flex flex-col lg:flex-row" data-testid="course-player">
      {/* SIDEBAR */}
      <aside className="w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-[#E0DCD1] bg-[#F7F6F0] lg:h-screen lg:sticky lg:top-0 overflow-y-auto" data-testid="course-sidebar">
        <div className="p-6 border-b border-[#E0DCD1]">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs font-mono-plex uppercase tracking-wider text-[#4B534E] hover:text-[#1A3626]" data-testid="back-to-dashboard">
            <ArrowLeft size={12} /> Dashboard
          </Link>
          <div className="font-serif-display text-xl text-[#121614] mt-3 leading-tight">{course.title}</div>
        </div>

        <div className="p-2">
          {course.modules.map((m) => (
            <div key={m.id} className="mb-2">
              <div className="px-3 py-2">
                <div className="font-mono-plex text-[10px] uppercase tracking-[0.18em] text-[#C56B52]">Module {m.number}</div>
                <div className="font-serif-display text-base text-[#121614] leading-tight mt-0.5">{m.title}</div>
              </div>
              <ul>
                {m.lessons.map((l) => {
                  const active = l.id === lessonId;
                  const done = progress.completed_lesson_ids.includes(l.id);
                  return (
                    <li key={l.id}>
                      <button
                        onClick={() => navigate(`/learn/${l.id}`)}
                        className={`w-full text-left px-3 py-2.5 flex items-start gap-2 text-sm border-l-2 transition-colors ${
                          active ? "border-[#C56B52] bg-white text-[#121614]" : "border-transparent text-[#4B534E] hover:bg-white"
                        }`}
                        data-testid={`sidebar-lesson-${l.id}`}
                      >
                        {done ? <CheckCircle2 size={14} className="text-[#1A3626] mt-0.5 flex-shrink-0" /> : <Circle size={14} className="mt-0.5 flex-shrink-0" />}
                        <span className="flex-1 leading-snug">{l.title}</span>
                        <span className="font-mono-plex text-[10px] text-[#4B534E]">{l.duration}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 bg-white">
        {lesson ? (
          <article className="max-w-3xl mx-auto p-8 lg:p-16" data-testid="lesson-content">
            <div className="overline mb-4">{lesson.module}</div>
            <h1 className="font-serif-display text-4xl lg:text-5xl text-[#121614] leading-tight mb-4">{lesson.title}</h1>
            <div className="font-mono-plex text-xs uppercase tracking-wider text-[#4B534E] mb-10">
              {lesson.duration} read
            </div>

            {/* Mock video frame */}
            <div className="aspect-video bg-[#1A3626] mb-12 flex items-center justify-center text-[#F7F6F0] relative overflow-hidden" data-testid="lesson-video-frame">
              <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: "radial-gradient(circle at 30% 30%, #264D37 0%, transparent 50%), radial-gradient(circle at 70% 70%, #C56B52 0%, transparent 40%)"
              }} />
              <div className="relative text-center">
                <div className="w-16 h-16 border-2 border-[#F7F6F0]/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-0 h-0 border-l-[14px] border-l-[#F7F6F0] border-y-[10px] border-y-transparent ml-1" />
                </div>
                <div className="font-mono-plex text-xs uppercase tracking-[0.2em] opacity-70">Lesson playback</div>
              </div>
            </div>

            <div className="prose-content text-lg leading-[1.8] text-[#121614] whitespace-pre-line">
              {lesson.content}
            </div>

            <div className="mt-16 pt-8 border-t border-[#E0DCD1]">
              <button
                onClick={toggleComplete}
                disabled={saving}
                className={isComplete ? "btn-secondary" : "btn-primary"}
                data-testid="mark-complete-button"
              >
                {isComplete ? <><Check size={16} /> Completed</> : <><Check size={16} /> Mark as complete</>}
              </button>
            </div>

            {/* PREV / NEXT */}
            <div className="grid grid-cols-2 gap-4 mt-10">
              {prevLesson ? (
                <button
                  onClick={() => navigate(`/learn/${prevLesson.id}`)}
                  className="card-flat p-5 text-left hover:bg-[#F7F6F0] transition-colors"
                  data-testid="prev-lesson-btn"
                >
                  <div className="flex items-center gap-2 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E] mb-2">
                    <ChevronLeft size={12} /> Previous
                  </div>
                  <div className="font-serif-display text-lg text-[#121614] leading-tight">{prevLesson.title}</div>
                </button>
              ) : <div />}
              {nextLesson ? (
                <button
                  onClick={() => navigate(`/learn/${nextLesson.id}`)}
                  className="card-flat p-5 text-left hover:bg-[#F7F6F0] transition-colors md:col-start-2"
                  data-testid="next-lesson-btn"
                >
                  <div className="flex items-center gap-2 font-mono-plex text-[10px] uppercase tracking-wider text-[#4B534E] mb-2 justify-end">
                    Next <ChevronRight size={12} />
                  </div>
                  <div className="font-serif-display text-lg text-[#121614] leading-tight text-right">{nextLesson.title}</div>
                </button>
              ) : <div />}
            </div>
          </article>
        ) : (
          <div className="p-16 font-mono-plex text-xs uppercase tracking-[0.3em] text-[#4B534E]">Select a lesson</div>
        )}
      </main>
    </div>
  );
}
